import { applyWatermarkToCanvas, type TWatermarkAsset } from '@/infrastructure/engine2d/export/applyWatermarkToCanvas';
import { canvasReduction } from '@/infrastructure/engine2d/export/canvasReduction';
import { EExportRenderingMode } from '@/application/ports/EExportRenderingMode';
import { getZoneCanvas } from '@/infrastructure/engine2d/export/exportZoneCanvas';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';

type TExportPipelineParams = {
    // Fill colour painted behind the zone snapshot before compositing.
    // Callers that will encode to JPEG should pass an opaque colour —
    // JPEG has no alpha and encodes transparent pixels as black.
    backgroundColor?: string;
    fabricWorld: FabricScene;
    mode: EExportRenderingMode;
    // Optional multiplier propagated to getZoneCanvas — currently
    // only used indirectly for scale (kept for parity with the old
    // signature; no caller sets it today).
    multiplier?: number;
    // Optional post-getZoneCanvas resize toward this pixel width.
    reduceToWidth?: number;
    // Optional watermark tiled over the whole canvas.
    watermark?: TWatermarkAsset;
};

// Single export pipeline shared by every FabricExporter2D method:
// getZoneCanvas (mode-driven overlay stripping) → optional watermark
// → optional reduction. Blob conversion is left to the caller so the
// same pipeline can feed both canvas-consumer paths (3D texture) and
// blob-consumer paths (cart PDF, thumbnails, BAT preview).
async function runExportPipeline({
    backgroundColor,
    fabricWorld,
    mode,
    multiplier,
    reduceToWidth,
    watermark,
}: TExportPipelineParams): Promise<HTMLCanvasElement> {
    let canvas = await getZoneCanvas({
        backgroundColor,
        fabricCanvas: fabricWorld.fabricCanvas,
        imageRenderHeight: fabricWorld.imageRenderHeight,
        imageRenderWidth: fabricWorld.imageRenderWidth,
        mode,
        multiplier,
    });
    if (watermark) {
        await _drawWatermark(canvas, watermark);
    }
    if (reduceToWidth) {
        canvas = canvasReduction(canvas, reduceToWidth);
    }
    return canvas;
}

// Convert an HTMLCanvasElement to a Blob with the given mime type and
// quality. Thin promisified wrapper around the callback-style canvas
// API — kept here so both the pipeline and its callers speak the
// same output primitive.
async function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/png' = 'image/png', quality = 1): Promise<Blob | null> {
    // canvas.toBlob dispatches its callback via the browser's event
    // loop and, on Chromium at least, gets starved when the main
    // thread is busy (Vue rendering, GC after canvasReduction churn)
    // — measured 130ms → 1000ms+ between the first thumbnail update
    // and the next. toDataURL is synchronous, then fetch decodes into
    // a Blob. Trade a marginal memory bump for consistent latency.
    const dataUrl = canvas.toDataURL(type, quality);
    const response = await fetch(dataUrl);
    return response.blob();
}

async function _drawWatermark(canvas: HTMLCanvasElement, watermark: TWatermarkAsset): Promise<void> {
    const image = await _loadImage(watermark.url);
    applyWatermarkToCanvas({
        clonedFabricCanvas: canvas,
        opacity: 1,
        scale: watermark.scale,
        watermarkImage: image,
        xOffset: watermark.size.width * watermark.scale.x + 100,
        yOffset: watermark.size.height * watermark.scale.y + 100,
    });
}

function _loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error(`runExportPipeline: watermark load failed for ${url}`));
        image.src = url;
    });
}

export { canvasToBlob, runExportPipeline };
